﻿/**
 * 审计模块衔接服务
 * 
 * 提供各审计模块之间的数据共享、事件通信和功能联动
 */

import { syncKanbanToServerWithFeedback } from './serverDataSync';
import { addLog } from './auditLogService';

// 事件总线 - 用于模块间通信
class AuditEventBus {
  constructor() {
    this.listeners = new Map();
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(cb => cb(data));
    }
  }
}

export const auditEventBus = new AuditEventBus();

// 事件类型常量
export const AUDIT_EVENTS = {
  // 项目相关
  PROJECT_CREATED: 'project:created',
  PROJECT_UPDATED: 'project:updated',
  PROJECT_DELETED: 'project:deleted',
  TASK_CREATED: 'task:created',
  TASK_UPDATED: 'task:updated',
  TASK_COMPLETED: 'task:completed',
  TASK_ASSIGNED: 'task:assigned',
  
  // 风险相关
  RISK_IDENTIFIED: 'risk:identified',
  RISK_UPDATED: 'risk:updated',
  RISK_MITIGATED: 'risk:mitigated',
  RISK_LINKED_TO_PROJECT: 'risk:linked_to_project',
  RISK_ASSESSMENT_COMPLETED: 'risk:assessment_completed',
  
  // 内控相关
  CONTROL_CREATED: 'control:created',
  CONTROL_UPDATED: 'control:updated',
  CONTROL_TESTED: 'control:tested',
  CONTROL_DEFICIENCY_FOUND: 'control:deficiency_found',
  
  // 底稿相关
  WORKPAPER_CREATED: 'workpaper:created',
  WORKPAPER_UPDATED: 'workpaper:updated',
  WORKPAPER_APPROVED: 'workpaper:approved',
  WORKPAPER_REJECTED: 'workpaper:rejected',
  
  // 整改相关
  RECTIFICATION_CREATED: 'rectification:created',
  RECTIFICATION_UPDATED: 'rectification:updated',
  RECTIFICATION_COMPLETED: 'rectification:completed',
  RECTIFICATION_VERIFIED: 'rectification:verified',
  
  // 人员相关（IPPF）
  AUDITOR_ASSIGNED: 'auditor:assigned',
  AUDITOR_SKILL_UPDATED: 'auditor:skill_updated',
  AUDITOR_ASSESSMENT_COMPLETED: 'auditor:assessment_completed',
  
  // 模块切换
  MODULE_SWITCH: 'module:switch',
  
  // OCR 相关
  OCR_COMPLETED: 'ocr:completed',
  
  // 数据同步
  DATA_SYNC: 'data:sync',
  DATA_EXPORT: 'data:export',
  DATA_IMPORT: 'data:import'
};

/**
 * 获取项目统计数据
 */
export function getProjectStats() {
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (!data) return { total: 0, inProgress: 0, completed: 0, tasks: 0 };
    
    const parsed = JSON.parse(data);
    const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
    
    let totalTasks = 0;
    let completedTasks = 0;
    
    projects.forEach(p => {
      const tasks = p.tasks || [];
      totalTasks += tasks.length;
      completedTasks += tasks.filter(t => t.stageId === 'completed' || t.status === 'completed').length;
    });
    
    return {
      total: projects.length,
      inProgress: projects.filter(p => p.status === 'inProgress' || !p.status).length,
      completed: projects.filter(p => p.status === 'completed').length,
      tasks: totalTasks,
      completedTasks
    };
  } catch (e) {
    return { total: 0, inProgress: 0, completed: 0, tasks: 0, completedTasks: 0 };
  }
}

// 与风险矩阵视图一致：等级与系数阈值（系数 = (可能性序号+1)*(影响序号+1)，1~25）
const LIKELIHOOD_LEVELS = ['极低', '低', '中', '高', '极高'];
const IMPACT_LEVELS = ['极低', '低', '中', '高', '极高'];
function normalizeLevelLabel(value) {
  if (value == null) return '中';
  const v = String(value).trim();
  const map = {
    '1': '极低', '2': '低', '3': '中', '4': '高', '5': '极高',
    '极低': '极低', '低': '低', '中': '中', '高': '高', '极高': '极高'
  };
  return map[v] || (LIKELIHOOD_LEVELS.includes(v) ? v : '中');
}
function normalizeRiskMatrixType(value) {
  const v = String(value || '').trim().toLowerCase();
  if (v === 'opportunity' || v === '机会') return 'opportunity';
  return 'risk';
}

/**
 * 获取风险矩阵统计数据（与双模型矩阵一致：风险高/中/低按系数≥20/≥12/&lt;12，并区分风险与机会）
 */
export function getRiskStats() {
  try {
    const data = localStorage.getItem('riskMatrixData');
    if (!data) return { total: 0, high: 0, medium: 0, low: 0, riskTotal: 0, opportunityTotal: 0 };
    
    const list = JSON.parse(data);
    if (!Array.isArray(list)) return { total: 0, high: 0, medium: 0, low: 0, riskTotal: 0, opportunityTotal: 0 };
    
    let high = 0, medium = 0, low = 0, riskTotal = 0, opportunityTotal = 0;
    
    for (let i = 0; i < list.length; i++) {
      const r = list[i];
      const type = normalizeRiskMatrixType(r.type || r['类型']);
      if (type === 'opportunity') {
        opportunityTotal++;
        continue;
      }
      riskTotal++;
      const likelihood = normalizeLevelLabel(r.likelihood ?? r['可能性'] ?? r['概率']);
      const impact = normalizeLevelLabel(r.impact ?? r['影响'] ?? r['影响程度']);
      const li = LIKELIHOOD_LEVELS.indexOf(likelihood);
      const ii = IMPACT_LEVELS.indexOf(impact);
      const factor = (li >= 0 ? li + 1 : 3) * (ii >= 0 ? ii + 1 : 3);
      if (factor >= 20) high++;
      else if (factor >= 12) medium++;
      else low++;
    }
    
    return {
      total: list.length,
      high,
      medium,
      low,
      riskTotal,
      opportunityTotal
    };
  } catch (e) {
    return { total: 0, high: 0, medium: 0, low: 0, riskTotal: 0, opportunityTotal: 0 };
  }
}

/**
 * 计算单条风险的等级（高/中/低）用于排序
 */
function getRiskLevelForSort(r) {
  const likelihood = normalizeLevelLabel(r.likelihood ?? r['可能性'] ?? r['概率']);
  const impact = normalizeLevelLabel(r.impact ?? r['影响'] ?? r['影响程度']);
  const li = LIKELIHOOD_LEVELS.indexOf(likelihood);
  const ii = IMPACT_LEVELS.indexOf(impact);
  const factor = (li >= 0 ? li + 1 : 3) * (ii >= 0 ? ii + 1 : 3);
  if (factor >= 20) return '高';
  if (factor >= 12) return '中';
  return '低';
}

const RISK_LEVEL_ORDER = { '高': 0, '中': 1, '低': 2 };

/**
 * 供 AI 对话使用的审计专栏模块数据摘要（用户选择模块后注入到上下文中，便于 AI 结合数据回复）
 * 支持按用户问题关键词做按需检索（如问「审计部」则只注入该部门数据），减少单次 token，降低 429。
 * @param {string} moduleId - 'risk-matrix' | 'internal-control' | 'audit-plans' | 'project-manager' | ''
 * @param {{ userMessage?: string }} options - userMessage 为当前用户问题，用于部门/环节过滤
 * @returns {string} 可注入 system 或首条 user 的文本
 */
const AI_CONTEXT_MAX_CHARS_RISK = 5500;
const AI_CONTEXT_MAX_CHARS_INTERNAL = 5500;
/** 用户问题中匹配到部门时，仅注入该部门数据时的条数上限 */
const AI_CONTEXT_FILTERED_RISK_MAX = 25;
const AI_CONTEXT_FILTERED_OPP_MAX = 15;
const AI_CONTEXT_FILTERED_CONTROL_MAX = 25;
/** 未过滤时：先给按部门/环节的统计摘要，再只列举前若干条 */
const AI_CONTEXT_SUMMARY_RISK_LIST = 15;
const AI_CONTEXT_SUMMARY_OPP_LIST = 10;
const AI_CONTEXT_SUMMARY_CONTROL_PER_STEP = 10;

function truncateWithNote(text, maxLen, label) {
  if (!text || text.length <= maxLen) return text;
  return text.slice(0, maxLen) + `\n...(已截断，超出 ${label} 长度限制，仅展示部分数据)`;
}

/** 从用户问题中提取部门关键词（用于风险矩阵过滤） */
function matchDepartmentFromMessage(userMessage) {
  if (!userMessage || typeof userMessage !== 'string') return null;
  const msg = userMessage.trim();
  const deptKeywords = ['审计部', '财务部', '人力资源', '人力部', '采购部', '销售部', '生产部', '运营部', '营运部', '物流部', '技术部', '研发部', '质量部', '安全部', '合规部', '战略部', '董事会', '管理层'];
  for (const kw of deptKeywords) {
    if (msg.includes(kw)) return kw;
  }
  return null;
}

/** 从用户问题中提取内控环节关键词 */
function matchInternalControlStepFromMessage(userMessage) {
  if (!userMessage || typeof userMessage !== 'string') return null;
  const msg = userMessage.trim();
  const stepKeywords = [
    { key: 'procurement', labels: ['采购'] },
    { key: 'grandparent', labels: ['祖代', '祖代鸡'] },
    { key: 'parent', labels: ['父母代', '父母代鸡'] },
    { key: 'commercial', labels: ['商品代', '商品代鸡'] },
    { key: 'slaughter', labels: ['屠宰'] },
    { key: 'deepProcess', labels: ['深加工', '食品加工'] },
    { key: 'logistics', labels: ['物流', '冷链'] },
    { key: 'sales', labels: ['销售'] },
    { key: 'hr', labels: ['人力', '人力资源'] },
    { key: 'finance', labels: ['财务'] },
    { key: 'operations', labels: ['营运', '运营'] },
  ];
  for (const { key, labels } of stepKeywords) {
    if (labels.some(l => msg.includes(l))) return key;
  }
  return null;
}

export function getAuditModuleContextForAI(moduleId, options = {}) {
  const { userMessage } = options || {};
  if (!moduleId) return '';
  if (moduleId === 'all') {
    const moduleIds = [
      'risk-matrix', 'internal-control', 'project-manager', 'audit-plans',
      'ippf', 'duties', 'supervision-audit', 'rectification-center',
      'engineering-audit', 'bidding-audit', 'it-audit',
    ];
    const parts = moduleIds.map(id => getAuditModuleContextForAI(id, options)).filter(Boolean);
    if (parts.length === 0) return '';
    const joined = parts.join('\n\n');
    const maxLen = 16000;
    return joined.length <= maxLen ? joined : joined.slice(0, maxLen) + '\n...(内容已截断，已展示主要模块摘要)';
  }
  try {
    if (moduleId === 'risk-matrix') {
      const raw = localStorage.getItem('riskMatrixData');
      if (!raw) return '【风险矩阵】当前无数据。';
      const list = JSON.parse(raw);
      if (!Array.isArray(list) || list.length === 0) return '【风险矩阵】当前无数据。';
      const withLevel = list.map(r => ({
        ...r,
        level: getRiskLevelForSort(r),
        type: normalizeRiskMatrixType(r.type || r['类型']),
        dept: (r.responsibleDept || r.riskManagementDept || r['责任部门'] || '').toString().trim() || '未分配部门',
        name: (r.name || r.title || r.thirdLevelRiskName || r.riskName || r['风险名称'] || r['名称'] || '').toString().trim() || '未命名',
        detail: (r.detail || r.description || r.thirdLevelRiskDesc || r['风险描述'] || r.desc || '').toString().trim() || '',
      }));
      const byLevel = (a, b) => (RISK_LEVEL_ORDER[a.level] ?? 2) - (RISK_LEVEL_ORDER[b.level] ?? 2);
      let risks = withLevel.filter(r => r.type === 'risk').sort(byLevel);
      let opportunities = withLevel.filter(r => r.type === 'opportunity').sort(byLevel);

      const deptFilter = matchDepartmentFromMessage(userMessage);
      let out = '【风险矩阵 - 供 AI 结合用户问题作答】\n';

      if (deptFilter) {
        risks = risks.filter(r => r.dept && r.dept.includes(deptFilter));
        opportunities = opportunities.filter(r => r.dept && r.dept.includes(deptFilter));
        out += `以下仅展示责任部门为「${deptFilter}」的数据。\n`;
        out += `该部门：风险 ${risks.length} 条，机会 ${opportunities.length} 条。\n`;
        const maxR = Math.min(risks.length, AI_CONTEXT_FILTERED_RISK_MAX);
        const maxO = Math.min(opportunities.length, AI_CONTEXT_FILTERED_OPP_MAX);
        out += '--- 风险 ---\n';
        risks.slice(0, maxR).forEach((r, i) => {
          out += `${i + 1}. ${r.dept} | ${r.level} | ${r.name}${r.detail ? ' | ' + r.detail.slice(0, 80) : ''}\n`;
        });
        if (risks.length > maxR) out += `... 共 ${risks.length} 条，仅展示前 ${maxR} 条\n`;
        out += '--- 机会 ---\n';
        opportunities.slice(0, maxO).forEach((r, i) => {
          out += `${i + 1}. ${r.dept} | ${r.level} | ${r.name}${r.detail ? ' | ' + r.detail.slice(0, 80) : ''}\n`;
        });
        if (opportunities.length > maxO) out += `... 共 ${opportunities.length} 条，仅展示前 ${maxO} 条\n`;
      } else {
        out += `风险共 ${risks.length} 条，机会共 ${opportunities.length} 条。\n`;
        const deptCounts = {};
        [...risks, ...opportunities].forEach(r => {
          const d = r.dept || '未分配部门';
          if (!deptCounts[d]) deptCounts[d] = { risk: 0, opp: 0, high: 0, mid: 0, low: 0 };
          if (r.type === 'risk') deptCounts[d].risk += 1; else deptCounts[d].opp += 1;
          if (r.level === '高') deptCounts[d].high += 1; else if (r.level === '中') deptCounts[d].mid += 1; else deptCounts[d].low += 1;
        });
        out += '按责任部门统计（部门 | 风险数 | 机会数 | 高/中/低）：\n';
        Object.entries(deptCounts).slice(0, 20).forEach(([d, c]) => {
          out += `  ${d}: 风险${c.risk} 机会${c.opp} (高${c.high} 中${c.mid} 低${c.low})\n`;
        });
        out += `\n列举（前${AI_CONTEXT_SUMMARY_RISK_LIST}条风险、前${AI_CONTEXT_SUMMARY_OPP_LIST}条机会）：\n`;
        out += '--- 风险 ---\n';
        risks.slice(0, AI_CONTEXT_SUMMARY_RISK_LIST).forEach((r, i) => {
          out += `${i + 1}. ${r.dept} | ${r.level} | ${r.name}${r.detail ? ' | ' + r.detail.slice(0, 60) : ''}\n`;
        });
        out += '--- 机会 ---\n';
        opportunities.slice(0, AI_CONTEXT_SUMMARY_OPP_LIST).forEach((r, i) => {
          out += `${i + 1}. ${r.dept} | ${r.level} | ${r.name}${r.detail ? ' | ' + r.detail.slice(0, 60) : ''}\n`;
        });
      }
      return truncateWithNote(out, AI_CONTEXT_MAX_CHARS_RISK, '风险矩阵');
    }
    if (moduleId === 'internal-control') {
      const raw = localStorage.getItem('broilerInternalControlData');
      if (!raw) return '【内控框架】当前无数据。';
      const data = JSON.parse(raw);
      const controls = data.customControls || {};
      const evaluations = data.controlEvaluations || {};
      const steps = ['procurement', 'grandparent', 'parent', 'commercial', 'slaughter', 'deepProcess', 'logistics', 'sales', 'hr', 'finance', 'operations'];
      const stepNames = { procurement: '采购管理', grandparent: '祖代鸡养殖', parent: '父母代鸡养殖', commercial: '商品代鸡养殖', slaughter: '屠宰加工', deepProcess: '食品深加工', logistics: '冷链物流', sales: '销售终端', hr: '人力管理', finance: '财务管理', operations: '营运管理' };

      const stepFilter = matchInternalControlStepFromMessage(userMessage);
      let out = '【内控框架 - 产业链环节控制与评价】\n';

      if (stepFilter && stepNames[stepFilter]) {
        const stepName = stepNames[stepFilter];
        const list = controls[stepFilter] || [];
        out += `以下仅展示「${stepName}」环节。共 ${list.length} 条控制活动。\n\n`;
        list.slice(0, AI_CONTEXT_FILTERED_CONTROL_MAX).forEach((c, i) => {
          const evalKey = `${stepFilter}_${c.id}`;
          const ev = evaluations[evalKey];
          out += `  ${i + 1}. [${c.id}] ${c.name} | 责任: ${c.responsible || '-'} | 风险: ${c.riskLevel || '-'}${ev?.result ? ' | 评价: ' + ev.result : ''}\n`;
        });
        if (list.length > AI_CONTEXT_FILTERED_CONTROL_MAX) out += `... 共 ${list.length} 条，仅展示前 ${AI_CONTEXT_FILTERED_CONTROL_MAX} 条\n`;
      } else {
        let totalControls = 0;
        steps.forEach(stepId => {
          totalControls += (controls[stepId] || []).length;
        });
        out += `合计约 ${totalControls} 条控制活动。各环节数量：\n`;
        steps.forEach(stepId => {
          const n = (controls[stepId] || []).length;
          if (n > 0) out += `  ${stepNames[stepId] || stepId}: ${n} 条\n`;
        });
        out += '\n列举（每环节前若干条）：\n';
        const maxPerStep = AI_CONTEXT_SUMMARY_CONTROL_PER_STEP;
        steps.forEach(stepId => {
          const stepName = stepNames[stepId] || stepId;
          const list = controls[stepId] || [];
          if (list.length === 0) return;
          out += `\n${stepName}：\n`;
          list.slice(0, maxPerStep).forEach((c, i) => {
            const evalKey = `${stepId}_${c.id}`;
            const ev = evaluations[evalKey];
            out += `  ${i + 1}. [${c.id}] ${c.name} | 责任: ${c.responsible || '-'} | 风险: ${c.riskLevel || '-'}${ev?.result ? ' | 评价: ' + ev.result : ''}\n`;
          });
          if (list.length > maxPerStep) out += `  ... 本环节共 ${list.length} 条\n`;
        });
      }
      return truncateWithNote(out, AI_CONTEXT_MAX_CHARS_INTERNAL, '内控框架');
    }
    if (moduleId === 'project-manager' || moduleId === 'audit-kanban') {
      const raw = localStorage.getItem('audit_kanban_data');
      if (!raw) return '【审计项目看板】当前无数据。';
      const parsed = JSON.parse(raw);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      let out = '【审计项目看板】\n';
      out += `项目共 ${projects.length} 个。\n`;
      projects.slice(0, 20).forEach((p, i) => {
        const title = p.title || p.name || '未命名';
        const status = p.status || p.stageId || '-';
        const tasks = (p.tasks || []).length;
        out += `${i + 1}. ${title} | 状态: ${status} | 任务数: ${tasks}\n`;
      });
      if (projects.length > 20) out += `... 共 ${projects.length} 个项目\n`;
      return out;
    }
    if (moduleId === 'audit-plans') {
      const raw = localStorage.getItem('audit_plans_backup') || localStorage.getItem('audit-plans');
      if (!raw) return '【审计计划】当前无数据（可从审计计划模块加载）。';
      try {
        const plans = JSON.parse(raw);
        const list = Array.isArray(plans) ? plans : (plans.list || plans.plans || []);
        let out = '【审计计划】\n';
        out += `计划共 ${list.length} 条。\n`;
        list.slice(0, 25).forEach((p, i) => {
          const name = p.name || p.title || p.planName || '未命名';
          const status = p.status || p.displayStatus || '-';
          out += `${i + 1}. ${name} | 状态: ${status}\n`;
        });
        if (list.length > 25) out += `... 共 ${list.length} 条\n`;
        return out;
      } catch (_) {
        return '【审计计划】当前无有效数据。';
      }
    }
    // ========== IPPF（审计人员能力） ==========
    if (moduleId === 'ippf') {
      const raw = localStorage.getItem('ippf_auditors_data');
      if (!raw) return '【IPPF 审计人员】当前无数据。';
      try {
        const list = JSON.parse(raw);
        if (!Array.isArray(list) || list.length === 0) return '【IPPF 审计人员】当前无数据。';
        let out = `【IPPF 审计人员】共 ${list.length} 人。\n`;
        list.slice(0, 20).forEach((a, i) => {
          const name = a.name || a.nickname || '未命名';
          const role = a.role || a.position || '-';
          const skills = Array.isArray(a.skills) ? a.skills.slice(0, 3).join('、') : '-';
          out += `${i + 1}. ${name} | 角色: ${role} | 技能: ${skills}\n`;
        });
        if (list.length > 20) out += `... 共 ${list.length} 人\n`;
        return out;
      } catch (_) { return '【IPPF 审计人员】数据异常。'; }
    }

    // ========== 职责分离 ==========
    if (moduleId === 'duties') {
      const raw = localStorage.getItem('incompatibleDutiesData');
      if (!raw) return '【职责分离】当前无数据。';
      try {
        const data = JSON.parse(raw);
        const modules = data.modules || data.businessModules || [];
        const matrix = data.matrix || data.dutyMatrix || [];
        let out = `【职责分离】\n`;
        out += `业务模块 ${Array.isArray(modules) ? modules.length : 0} 个`;
        if (Array.isArray(matrix)) out += `，分离矩阵 ${matrix.length} 条`;
        out += '。\n';
        if (Array.isArray(modules)) {
          modules.slice(0, 15).forEach((m, i) => {
            const name = typeof m === 'string' ? m : (m.name || m.title || '未命名');
            out += `${i + 1}. ${name}\n`;
          });
        }
        return out;
      } catch (_) { return '【职责分离】数据异常。'; }
    }

    // ========== 监察审计 ==========
    if (moduleId === 'supervision-audit') {
      let out = '【监察审计】\n';
      const complaints = JSON.parse(localStorage.getItem('supervision_complaints') || '[]');
      const tips = JSON.parse(localStorage.getItem('supervision_tips') || '[]');
      const rects = JSON.parse(localStorage.getItem('supervision_rectification') || '[]');
      const notices = JSON.parse(localStorage.getItem('supervision_notices') || '[]');
      out += `投诉 ${complaints.length} 条，线索 ${tips.length} 条，整改督办 ${rects.length} 条，公示公告 ${notices.length} 条。\n`;
      if (complaints.length) { out += '最近投诉：\n'; complaints.slice(0, 8).forEach((c, i) => { out += `  ${i + 1}. ${c.title || c.content?.slice(0, 40) || '无标题'} | 状态: ${c.status || '-'}\n`; }); }
      if (tips.length) { out += '最近线索：\n'; tips.slice(0, 8).forEach((t, i) => { out += `  ${i + 1}. ${t.title || t.content?.slice(0, 40) || '无标题'} | 状态: ${t.status || '-'}\n`; }); }
      return truncateWithNote(out, 3000, '监察审计');
    }

    // ========== 整改中心 ==========
    if (moduleId === 'rectification-center') {
      const raw = localStorage.getItem('rectification_center_data');
      if (!raw) return '【整改中心】当前无数据。';
      try {
        const list = JSON.parse(raw);
        const items = Array.isArray(list) ? list : (list?.items || []);
        if (items.length === 0) return '【整改中心】当前无数据。';
        const byStatus = {};
        items.forEach(r => { const s = r.status || '未知'; byStatus[s] = (byStatus[s] || 0) + 1; });
        let out = `【整改中心】共 ${items.length} 条整改事项。\n`;
        out += '按状态统计：' + Object.entries(byStatus).map(([s, n]) => `${s}: ${n}`).join('，') + '\n';
        items.slice(0, 15).forEach((r, i) => {
          const title = r.title || r.issueDescription || '无标题';
          out += `${i + 1}. ${title.slice(0, 50)} | 状态: ${r.status || '-'} | 来源: ${r.sourceType || '-'}\n`;
        });
        if (items.length > 15) out += `... 共 ${items.length} 条\n`;
        return out;
      } catch (_) { return '【整改中心】数据异常。'; }
    }

    // ========== 工程审计 ==========
    if (moduleId === 'engineering-audit') {
      let out = '【工程审计】\n';
      const projects = JSON.parse(localStorage.getItem('engineering_audit_projects') || '[]');
      const contracts = JSON.parse(localStorage.getItem('engineering_audit_contracts') || '[]');
      const findings = JSON.parse(localStorage.getItem('engineering_audit_findings') || '[]');
      const costs = JSON.parse(localStorage.getItem('engineering_audit_costs') || '[]');
      out += `项目 ${projects.length}，合同 ${contracts.length}，审计发现 ${findings.length}，造价 ${costs.length}。\n`;
      if (projects.length) {
        out += '项目清单：\n';
        projects.slice(0, 10).forEach((p, i) => {
          out += `  ${i + 1}. ${p.name || p.title || '未命名'} | 状态: ${p.status || '-'}\n`;
        });
        if (projects.length > 10) out += `  ... 共 ${projects.length} 个\n`;
      }
      if (findings.length) {
        out += '审计发现（前10条）：\n';
        findings.slice(0, 10).forEach((f, i) => {
          out += `  ${i + 1}. ${(f.title || f.description || '无描述').slice(0, 50)} | 级别: ${f.severity || f.level || '-'}\n`;
        });
      }
      return truncateWithNote(out, 3500, '工程审计');
    }

    if (moduleId === 'bidding-audit') {
      const bids = JSON.parse(localStorage.getItem('engineering_audit_bid_supervision') || '[]');
      let out = `【招投标与监标】共 ${bids.length} 条台账。\n`;
      (Array.isArray(bids) ? bids : []).slice(0, 15).forEach((r, i) => {
        const name = (r.projectName || '未命名').slice(0, 40);
        out += `  ${i + 1}. ${name} | ${r.year || '-'} | ${r.status || '-'}\n`;
      });
      return truncateWithNote(out, 2500, '招投标与监标');
    }

    // ========== IT审计 ==========
    if (moduleId === 'it-audit') {
      let out = '【IT审计】\n';
      const projects = JSON.parse(localStorage.getItem('it_audit_projects') || '[]');
      const systems = JSON.parse(localStorage.getItem('it_audit_systems') || '[]');
      const findings = JSON.parse(localStorage.getItem('it_audit_findings') || '[]');
      const checklists = JSON.parse(localStorage.getItem('it_audit_checklists') || '[]');
      out += `项目 ${projects.length}，系统 ${systems.length}，审计发现 ${findings.length}，检查清单 ${checklists.length}。\n`;
      if (projects.length) {
        out += '项目清单：\n';
        projects.slice(0, 10).forEach((p, i) => {
          out += `  ${i + 1}. ${p.name || p.title || '未命名'} | 状态: ${p.status || '-'}\n`;
        });
      }
      if (findings.length) {
        out += '审计发现（前10条）：\n';
        findings.slice(0, 10).forEach((f, i) => {
          out += `  ${i + 1}. ${(f.title || f.description || '无描述').slice(0, 50)} | 级别: ${f.severity || f.level || '-'}\n`;
        });
      }
      return truncateWithNote(out, 3500, 'IT审计');
    }

    return '';
  } catch (e) {
    return `【${moduleId}】获取数据时出错: ${e.message}`;
  }
}

/**
 * 获取审计专栏可供 AI 选择的模块列表（用于下拉）
 */
export function getAuditModulesForAISelector() {
  return [
    { id: '', name: '不结合数据' },
    { id: 'all', name: '全部系统数据' },
    { id: 'risk-matrix', name: '风险-矩阵' },
    { id: 'internal-control', name: '内控框架' },
    { id: 'project-manager', name: '审计项目看板' },
    { id: 'audit-plans', name: '审计计划' },
    { id: 'ippf', name: 'IPPF 审计人员' },
    { id: 'duties', name: '职责分离' },
    { id: 'supervision-audit', name: '监察审计' },
    { id: 'rectification-center', name: '整改中心' },
    { id: 'engineering-audit', name: '工程审计' },
    { id: 'bidding-audit', name: '招投标与监标' },
    { id: 'it-audit', name: 'IT审计' },
  ];
}

/**
 * 根据关键词检索系统数据，供 AI 对话输入框快捷填入（覆盖所有主要模块）
 */
export function searchSystemDataForAI(keyword) {
  const k = (keyword || '').toString().trim();
  if (!k) return [];
  const lower = k.toLowerCase();
  const results = [];
  const maxPerType = 4;

  const searchList = (raw, nameExtractor, labelPrefix, type) => {
    try {
      if (!raw) return;
      const list = JSON.parse(raw);
      const arr = Array.isArray(list) ? list : (list?.items || list?.projects || list?.list || list?.plans || []);
      arr.filter(item => nameExtractor(item).toLowerCase().includes(lower)).slice(0, maxPerType).forEach(item => {
        const name = nameExtractor(item) || '未命名';
        results.push({ label: `${labelPrefix}${name}`, value: name, type });
      });
    } catch (_) {}
  };

  searchList(localStorage.getItem('audit_plans_backup') || localStorage.getItem('audit-plans'), p => p.name || p.title || p.planName || '', '审计计划：', 'audit-plan');
  searchList(localStorage.getItem('audit_kanban_data'), p => p.title || p.name || '', '项目：', 'project');
  searchList(localStorage.getItem('riskMatrixData'), r => r.name || r.title || r.thirdLevelRiskName || r.riskName || r['风险名称'] || '', '风险/机会：', 'risk');
  searchList(localStorage.getItem('ippf_auditors_data'), a => a.name || a.nickname || '', 'IPPF人员：', 'ippf');
  searchList(localStorage.getItem('engineering_audit_projects'), p => p.name || p.title || '', '工程项目：', 'eng-project');
  searchList(localStorage.getItem('it_audit_projects'), p => p.name || p.title || '', 'IT审计：', 'it-project');
  searchList(localStorage.getItem('rectification_center_data'), r => r.title || r.issueDescription || '', '整改：', 'rectification');
  searchList(localStorage.getItem('supervision_complaints'), c => c.title || c.content?.slice(0, 30) || '', '投诉：', 'complaint');
  searchList(localStorage.getItem('supervision_tips'), t => t.title || t.content?.slice(0, 30) || '', '线索：', 'tip');

  try {
    const internalRaw = localStorage.getItem('broilerInternalControlData');
    if (internalRaw) {
      const data = JSON.parse(internalRaw);
      const controls = data.customControls || {};
      let count = 0;
      for (const stepId of Object.keys(controls)) {
        if (count >= maxPerType) break;
        (controls[stepId] || []).filter(c => (c.name || '').toString().toLowerCase().includes(lower)).slice(0, maxPerType - count).forEach(c => {
          results.push({ label: `内控：${c.name}`, value: c.name, type: 'control' });
          count++;
        });
      }
    }
  } catch (_) {}

  return results.slice(0, 15);
}

export function getWorkpaperStats() {
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (!data) return { total: 0, draft: 0, approved: 0 };
    
    const parsed = JSON.parse(data);
    const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
    
    let total = 0, draft = 0, approved = 0;
    
    projects.forEach(p => {
      const items = p.workpapers?.items || [];
      total += items.length;
      draft += items.filter(w => w.status === 'draft').length;
      approved += items.filter(w => w.status === 'approved').length;
    });
    
    return { total, draft, approved };
  } catch (e) {
    return { total: 0, draft: 0, approved: 0 };
  }
}

/**
 * 获取整改统计数据
 */
export function getRectificationStats() {
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (!data) return { total: 0, pending: 0, completed: 0 };
    
    const parsed = JSON.parse(data);
    const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
    
    let total = 0, pending = 0, completed = 0;
    
    projects.forEach(p => {
      const rects = p.rectifications || [];
      total += rects.length;
      pending += rects.filter(r => r.status === 'pending' || r.status === 'inProgress').length;
      completed += rects.filter(r => r.status === 'completed' || r.status === 'verified').length;
    });
    
    return { total, pending, completed };
  } catch (e) {
    return { total: 0, pending: 0, completed: 0 };
  }
}

/**
 * 从风险矩阵创建审计任务
 */
export function createTaskFromRisk(risk, projectId = null) {
  const task = {
    id: `task-risk-${Date.now()}`,
    title: `风险审计: ${risk.thirdLevelRiskName || risk.name || risk.title || '未命名风险'}`,
    description: `风险描述: ${risk.thirdLevelRiskDesc || risk.description || ''}\n风险等级: ${risk.riskLevel?.level || '未评估'}\n可能性: ${risk.likelihood || 0}\n影响程度: ${risk.impact || 0}`,
    stageId: 'planning',
    priority: (risk.likelihood || 0) * (risk.impact || 0) > 12 ? 'high' : (risk.likelihood || 0) * (risk.impact || 0) > 6 ? 'medium' : 'low',
    tags: [{ id: 'risk', name: '风险审计', color: '#ef4444' }],
    sourceType: 'risk',
    sourceId: risk.id || risk.thirdLevelRiskId,
    createdAt: new Date().toISOString()
  };
  
  // 尝试保存到第一个项目（如果没有指定项目）
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (data) {
      const parsed = JSON.parse(data);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      
      if (projects.length > 0) {
        const targetProjectId = projectId || projects[0].id;
        const projectIndex = projects.findIndex(p => p.id === targetProjectId);
        
        if (projectIndex >= 0) {
          if (!projects[projectIndex].tasks) {
            projects[projectIndex].tasks = [];
          }
          projects[projectIndex].tasks.push(task);
          projects[projectIndex].updatedAt = new Date().toISOString();
          localStorage.setItem('audit_kanban_data', JSON.stringify(parsed));
          syncKanbanToServerWithFeedback();
        }
      }
    }
  } catch (e) {
    console.error('保存任务失败:', e);
  }
  
  auditEventBus.emit(AUDIT_EVENTS.PROJECT_UPDATED, { projectId, task });
  return task;
}

/**
 * 从整改事项创建跟踪任务
 */
export function createTaskFromRectification(rectification, projectId) {
  const task = {
    id: `task-rect-${Date.now()}`,
    title: `整改跟踪: ${rectification.issueNo}`,
    description: rectification.issueDescription,
    stageId: 'inProgress',
    priority: 'high',
    dueDate: rectification.deadline,
    tags: [{ id: 'rectification', name: '整改跟踪', color: '#f59e0b' }],
    sourceType: 'rectification',
    sourceId: rectification.id,
    createdAt: new Date().toISOString()
  };
  
  auditEventBus.emit(AUDIT_EVENTS.PROJECT_UPDATED, { projectId, task });
  return task;
}

/**
 * 从风险创建整改事项
 */
export function createRectificationFromRisk(risk, projectId, findings) {
  const rectification = {
    id: `rect-${Date.now()}`,
    issueNo: `RECT-${Date.now().toString().slice(-6)}`,
    issueDescription: findings || `发现风险: ${risk.thirdLevelRiskName || risk.name}`,
    riskLevel: risk.riskLevel?.level || '中',
    responsibleDept: risk.responsibleDept || risk.listDept || '未指定',
    deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'pending',
    sourceType: 'risk',
    sourceId: risk.id || risk.thirdLevelRiskId,
    createdAt: new Date().toISOString(),
    progressHistory: []
  };
  
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (data) {
      const parsed = JSON.parse(data);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      const targetProjectId = projectId || projects[0]?.id;
      const projectIndex = projects.findIndex(p => p.id === targetProjectId);
      
      if (projectIndex >= 0) {
        if (!projects[projectIndex].rectifications) {
          projects[projectIndex].rectifications = [];
        }
        projects[projectIndex].rectifications.push(rectification);
        projects[projectIndex].updatedAt = new Date().toISOString();
        localStorage.setItem('audit_kanban_data', JSON.stringify(parsed));
        syncKanbanToServerWithFeedback();
        
        auditEventBus.emit(AUDIT_EVENTS.RECTIFICATION_CREATED, { projectId: targetProjectId, rectification, risk });
      }
    }
  } catch (e) {
    console.error('创建整改事项失败:', e);
  }
  
  return rectification;
}

/**
 * 关联风险到项目
 */
export function linkRiskToProject(riskId, projectId) {
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (!data) return false;
    
    const parsed = JSON.parse(data);
    const project = parsed.projects?.find(p => p.id === projectId);
    if (!project) return false;
    
    if (!project.linkedRisks) project.linkedRisks = [];
    if (!project.linkedRisks.includes(riskId)) {
      project.linkedRisks.push(riskId);
      localStorage.setItem('audit_kanban_data', JSON.stringify(parsed));
      syncKanbanToServerWithFeedback();
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * 获取项目关联的风险
 */
export function getProjectLinkedRisks(projectId) {
  try {
    const projectData = localStorage.getItem('audit_kanban_data');
    const riskData = localStorage.getItem('riskMatrixData');
    
    if (!projectData || !riskData) return [];
    
    const parsed = JSON.parse(projectData);
    const project = parsed.projects?.find(p => p.id === projectId);
    if (!project?.linkedRisks) return [];
    
    const risks = JSON.parse(riskData);
    return risks.filter(r => project.linkedRisks.includes(r.id));
  } catch (e) {
    return [];
  }
}

/**
 * 获取综合审计仪表盘数据
 */
export function getDashboardData() {
  return {
    projects: getProjectStats(),
    risks: getRiskStats(),
    workpapers: getWorkpaperStats(),
    rectifications: getRectificationStats(),
    lastUpdated: new Date().toISOString()
  };
}

// --------------- 决策支持：聚合审计核心各功能数据 ---------------

/** 决策支持 - 获取看板项目列表（审计核心-项目管理） */
export function getDecisionSupportCoreProjects() {
  try {
    const raw = localStorage.getItem('audit_kanban_data');
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data) ? data : (data.projects || []);
  } catch (e) {
    return [];
  }
}

/** 决策支持 - 获取审计计划列表（审计核心-审计计划） */
export function getDecisionSupportPlans() {
  try {
    const raw = localStorage.getItem('audit_plans_data');
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

/** 决策支持 - 合并各来源的审计发现（审计执行 + 工程审计 + IT审计） */
export function getDecisionSupportFindings() {
  const list = [];
  try {
    const core = JSON.parse(localStorage.getItem('audit_findings') || '[]');
    (Array.isArray(core) ? core : []).forEach((f) => list.push({ ...f, _source: '审计执行' }));
  } catch (e) {}
  try {
    const eng = JSON.parse(localStorage.getItem('engineering_audit_findings') || '[]');
    (Array.isArray(eng) ? eng : []).forEach((f) => list.push({ ...f, _source: '工程审计' }));
  } catch (e) {}
  try {
    const it = JSON.parse(localStorage.getItem('it_audit_findings') || '[]');
    (Array.isArray(it) ? it : []).forEach((f) => list.push({ ...f, _source: 'IT审计' }));
  } catch (e) {}
  return list;
}

/** 决策支持 - 用户列表（审计核心-用户管理），用于机构人员统计 */
export function getDecisionSupportUsers() {
  try {
    const raw = localStorage.getItem('audit_portal_users_db');
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

/** 决策支持 - 看板项目按状态统计（与 getProjectStats 一致，返回按 status 分组数量） */
export function getDecisionSupportProjectByStatus() {
  const projects = getDecisionSupportCoreProjects();
  const byStatus = {};
  projects.forEach((p) => {
    const s = p.status || 'inProgress';
    byStatus[s] = (byStatus[s] || 0) + 1;
  });
  return { byStatus, total: projects.length };
}

/** 决策支持 - 合并发现按严重程度与来源统计 */
export function getDecisionSupportFindingStats() {
  const list = getDecisionSupportFindings();
  const bySeverity = {};
  const bySource = {};
  list.forEach((f) => {
    const sev = f.severity || 'medium';
    bySeverity[sev] = (bySeverity[sev] || 0) + 1;
    const src = f._source || '审计执行';
    bySource[src] = (bySource[src] || 0) + 1;
  });
  return { total: list.length, bySeverity, bySource };
}

/**
 * 导航到指定模块（并记录审计日志）
 */
export function navigateToModule(moduleId, params = {}) {
  addLog('view', moduleId || 'system', { params }).catch(() => {});
  auditEventBus.emit(AUDIT_EVENTS.MODULE_SWITCH, { moduleId, params });
}

/** Cherry / 技能库等工作流动作的浏览器事件名；由 InternalAuditPortal 捕获并跳转 */
export const AUDIT_PORTAL_WORKFLOW_ACTION_EVENT = 'audit-portal-run-workflow-action';

/**
 * 执行编排动作（一键工作流）：打开模块（带 params）、AI 工作台预填、或 Agent 技能库定位。
 * @param {Record<string, unknown> | null | undefined} action
 */
export function runWorkflowAction(action) {
  if (!action || typeof action !== 'object') return;
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(AUDIT_PORTAL_WORKFLOW_ACTION_EVENT, { detail: action }));
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * 搜索所有模块数据
 */
export function globalSearch(keyword) {
  const text = String(keyword || '').trim();
  if (!text) return [];
  const lowerKeyword = text.toLowerCase();
  const results = [];
  const dedupe = new Set();
  const pushResult = (item) => {
    const key = `${item.module || ''}|${item.type || ''}|${item.id || item.title || ''}`;
    if (dedupe.has(key)) return;
    dedupe.add(key);
    results.push(item);
  };
  const extractArray = (parsed) => {
    if (Array.isArray(parsed)) return parsed;
    if (!parsed || typeof parsed !== 'object') return [];
    return (
      parsed.items
      || parsed.projects
      || parsed.list
      || parsed.plans
      || parsed.records
      || parsed.reports
      || parsed.data
      || []
    );
  };
  const pickFirstText = (obj, keys = []) => {
    for (const key of keys) {
      const value = obj?.[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  };
  const searchSource = ({
    storageKey,
    module,
    type,
    titleKeys = [],
    extraTextKeys = [],
    fallbackTitle = '未命名记录',
    mapItem,
  }) => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const arr = extractArray(parsed);
      (Array.isArray(arr) ? arr : []).forEach((item, index) => {
        const title = pickFirstText(item, titleKeys);
        const extraText = extraTextKeys
          .map((key) => {
            const value = item?.[key];
            if (value == null) return '';
            if (typeof value === 'string') return value;
            if (typeof value === 'number' || typeof value === 'boolean') return String(value);
            return '';
          })
          .filter(Boolean)
          .join(' ');
        const haystack = `${title} ${extraText}`.toLowerCase();
        if (!haystack.includes(lowerKeyword)) return;
        const base = {
          type,
          id: item?.id || `${storageKey}-${index}-${title || fallbackTitle}`,
          title: title || fallbackTitle,
          module,
        };
        pushResult(typeof mapItem === 'function' ? mapItem(item, base) : base);
      });
    } catch (_) {}
  };

  // 搜索项目/任务
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (data) {
      const parsed = JSON.parse(data);
      const projects = Array.isArray(parsed) ? parsed : (parsed?.projects || []);
      projects.forEach((p) => {
        const projectName = String(p?.name || p?.title || '').trim();
        if (projectName.toLowerCase().includes(lowerKeyword)) {
          pushResult({
            type: 'project',
            id: p.id,
            title: projectName,
            module: 'project-manager',
            projectId: p.id,
            navigateParams: { projectId: p.id }
          });
        }
        (Array.isArray(p?.tasks) ? p.tasks : []).forEach((t) => {
          const taskTitle = String(t?.title || '').trim();
          if (taskTitle.toLowerCase().includes(lowerKeyword)) {
            pushResult({
              type: 'task',
              id: t.id,
              title: taskTitle,
              projectId: p.id,
              module: 'project-manager',
              navigateParams: { projectId: p.id, taskId: t.id || '' }
            });
          }
        });
      });
    }
  } catch (e) {}

  // 搜索风险/机会
  try {
    const data = localStorage.getItem('riskMatrixData');
    if (data) {
      const risks = JSON.parse(data);
      (Array.isArray(risks) ? risks : []).forEach((r) => {
        const name = String(r?.thirdLevelRisk || r?.name || r?.title || r?.riskName || '').trim();
        const desc = String(r?.thirdLevelRiskDesc || r?.description || r?.detail || '').trim();
        if (name.toLowerCase().includes(lowerKeyword) || desc.toLowerCase().includes(lowerKeyword)) {
          pushResult({
            type: 'risk',
            id: r.id || `${name}-${desc}`,
            title: name || '未命名风险',
            module: 'risk-matrix',
            navigateParams: { focusRiskId: r.id || r.thirdLevelRiskId || '' },
          });
        }
      });
    }
  } catch (e) {}

  searchSource({
    storageKey: 'audit_plans_data',
    module: 'audit-plans',
    type: 'plan',
    titleKeys: ['name', 'title', 'planName'],
    extraTextKeys: ['description', 'goal', 'owner', 'department', 'status'],
    fallbackTitle: '未命名审计计划',
  });

  searchSource({
    storageKey: 'rectification_center_data',
    module: 'rectification-center',
    type: 'rectification',
    titleKeys: ['title', 'issueDescription', 'problem'],
    extraTextKeys: ['status', 'sourceType', 'responsibleDept', 'owner', 'description'],
    fallbackTitle: '未命名整改事项',
    mapItem: (item, base) => ({
      ...base,
      navigateParams: { focusRectId: item?.id || item?.rectId || '' },
    }),
  });

  searchSource({
    storageKey: 'supervision_complaints',
    module: 'supervision-audit',
    type: 'complaint',
    titleKeys: ['title', 'subject'],
    extraTextKeys: ['content', 'category', 'status', 'department'],
    fallbackTitle: '未命名投诉',
  });

  searchSource({
    storageKey: 'supervision_tips',
    module: 'supervision-audit',
    type: 'tip',
    titleKeys: ['title', 'subject'],
    extraTextKeys: ['content', 'category', 'status', 'department'],
    fallbackTitle: '未命名线索',
  });

  searchSource({
    storageKey: 'reportDashboardReports',
    module: 'report-dashboard',
    type: 'report',
    titleKeys: ['title', 'name', 'reportName', 'topic'],
    extraTextKeys: ['summary', 'content', 'author', 'type'],
    fallbackTitle: '未命名报告',
  });

  searchSource({
    storageKey: 'engineering_audit_projects',
    module: 'engineering-audit',
    type: 'engineering-project',
    titleKeys: ['name', 'title', 'projectName'],
    extraTextKeys: ['status', 'manager', 'department', 'description'],
    fallbackTitle: '未命名工程项目',
  });

  searchSource({
    storageKey: 'it_audit_projects',
    module: 'it-audit',
    type: 'it-project',
    titleKeys: ['name', 'title', 'projectName'],
    extraTextKeys: ['status', 'manager', 'department', 'description'],
    fallbackTitle: '未命名 IT 审计项目',
  });

  // 搜索模块名称（便于直接跳转）
  const moduleEntries = [
    { module: 'project-manager', title: '项目管理' },
    { module: 'risk-matrix', title: '风险机会矩阵' },
    { module: 'audit-plans', title: '审计计划' },
    { module: 'internal-control', title: '内控管理' },
    { module: 'rectification-center', title: '整改中心' },
    { module: 'report-dashboard', title: '汇报报告' },
    { module: 'user-management', title: '用户管理' },
    { module: 'engineering-audit', title: '工程审计' },
    { module: 'bidding-audit', title: '招投标与监标' },
    { module: 'it-audit', title: 'IT审计' },
    { module: 'supervision-audit', title: '监察审计' },
    { module: 'notification-center', title: '通知中心' },
  ];
  moduleEntries.forEach((item) => {
    if (item.title.toLowerCase().includes(lowerKeyword)) {
      pushResult({
        type: 'module',
        id: item.module,
        title: `进入模块：${item.title}`,
        module: item.module,
      });
    }
  });

  return results.slice(0, 30);
}

/**
 * 获取IPPF人员统计数据
 */
export function getIPPFStats() {
  try {
    const data = localStorage.getItem('ippf_auditors_data');
    if (!data) return { total: 0, byLevel: {}, byGroup: {} };
    
    const auditors = JSON.parse(data);
    const byLevel = {};
    const byGroup = {};
    
    auditors.forEach(a => {
      byLevel[a.level] = (byLevel[a.level] || 0) + 1;
      if (a.groupId) {
        byGroup[a.groupId] = (byGroup[a.groupId] || 0) + 1;
      }
    });
    
    return { total: auditors.length, byLevel, byGroup };
  } catch (e) {
    return { total: 0, byLevel: {}, byGroup: {} };
  }
}

/**
 * 从风险创建内控测试任务
 */
export function createControlTestFromRisk(risk, projectId) {
  const task = {
    id: `task-control-${Date.now()}`,
    title: `内控测试: ${risk.internalControl || '相关控制'}`,
    description: `风险: ${risk.thirdLevelRiskName || risk.name}\n内控措施: ${risk.internalControl || '未指定'}\n测试目标: 验证控制有效性`,
    stageId: 'testing',
    priority: risk.riskLevel?.level === '极高' || risk.riskLevel?.level === '高' ? 'high' : 'medium',
    tags: [
      { id: 'control-test', name: '内控测试', color: '#8b5cf6' },
      { id: 'risk-based', name: '风险导向', color: '#ef4444' }
    ],
    sourceType: 'risk',
    sourceId: risk.id || risk.thirdLevelRiskId,
    controlInfo: {
      controlName: risk.internalControl,
      riskLevel: risk.riskLevel?.level,
      department: risk.responsibleDept
    },
    createdAt: new Date().toISOString()
  };
  
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (data) {
      const parsed = JSON.parse(data);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      const targetProjectId = projectId || projects[0]?.id;
      const projectIndex = projects.findIndex(p => p.id === targetProjectId);
      
      if (projectIndex >= 0) {
        if (!projects[projectIndex].tasks) projects[projectIndex].tasks = [];
        projects[projectIndex].tasks.push(task);
        projects[projectIndex].updatedAt = new Date().toISOString();
        localStorage.setItem('audit_kanban_data', JSON.stringify(parsed));
        syncKanbanToServerWithFeedback();
        
        auditEventBus.emit(AUDIT_EVENTS.TASK_CREATED, { projectId: targetProjectId, task });
        auditEventBus.emit(AUDIT_EVENTS.CONTROL_TESTED, { risk, task });
      }
    }
  } catch (e) {
    console.error('创建内控测试任务失败:', e);
  }
  
  return task;
}

/**
 * 分配审计人员到项目
 */
export function assignAuditorToProject(auditorId, projectId, role = 'member') {
  try {
    const projectData = localStorage.getItem('audit_kanban_data');
    const auditorData = localStorage.getItem('ippf_auditors_data');
    
    if (!projectData || !auditorData) return false;
    
    const parsed = JSON.parse(projectData);
    const auditors = JSON.parse(auditorData);
    
    const project = parsed.projects?.find(p => p.id === projectId);
    const auditor = auditors.find(a => a.id === auditorId);
    
    if (!project || !auditor) return false;
    
    if (!project.team) project.team = [];
    
    const existing = project.team.find(m => m.auditorId === auditorId);
    if (existing) {
      existing.role = role;
      existing.updatedAt = new Date().toISOString();
    } else {
      project.team.push({
        auditorId,
        name: auditor.name,
        role,
        assignedAt: new Date().toISOString()
      });
    }
    
    project.updatedAt = new Date().toISOString();
    localStorage.setItem('audit_kanban_data', JSON.stringify(parsed));
    syncKanbanToServerWithFeedback();
    
    auditEventBus.emit(AUDIT_EVENTS.AUDITOR_ASSIGNED, { auditorId, projectId, role, auditor });
    return true;
  } catch (e) {
    console.error('分配审计人员失败:', e);
    return false;
  }
}

/**
 * 获取审计人员的项目列表
 */
export function getAuditorProjects(auditorId) {
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (!data) return [];
    
    const parsed = JSON.parse(data);
    const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
    
    return projects.filter(p => 
      p.team?.some(m => m.auditorId === auditorId)
    ).map(p => ({
      id: p.id,
      name: p.name,
      role: p.team.find(m => m.auditorId === auditorId)?.role,
      status: p.status,
      taskCount: p.tasks?.length || 0
    }));
  } catch (e) {
    return [];
  }
}

/**
 * 从审计发现创建整改事项（增强版）
 */
export function createRectificationFromFinding(finding, projectId) {
  const rectification = {
    id: `rect-${Date.now()}`,
    issueNo: `RECT-${Date.now().toString().slice(-6)}`,
    issueDescription: finding.description,
    findingId: finding.id,
    findingType: finding.type,
    riskLevel: finding.severity,
    responsibleDept: finding.responsibleDept || '未指定',
    deadline: finding.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'pending',
    impact: finding.impact,
    recommendation: finding.recommendation,
    sourceType: 'finding',
    sourceId: finding.id,
    createdAt: new Date().toISOString(),
    progressHistory: []
  };
  
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (data) {
      const parsed = JSON.parse(data);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      const targetProjectId = projectId || projects[0]?.id;
      const projectIndex = projects.findIndex(p => p.id === targetProjectId);
      
      if (projectIndex >= 0) {
        if (!projects[projectIndex].rectifications) {
          projects[projectIndex].rectifications = [];
        }
        projects[projectIndex].rectifications.push(rectification);
        projects[projectIndex].updatedAt = new Date().toISOString();
        localStorage.setItem('audit_kanban_data', JSON.stringify(parsed));
        syncKanbanToServerWithFeedback();
        
        auditEventBus.emit(AUDIT_EVENTS.RECTIFICATION_CREATED, { 
          projectId: targetProjectId, 
          rectification, 
          finding 
        });
      }
    }
  } catch (e) {
    console.error('创建整改事项失败:', e);
  }
  
  return rectification;
}

/**
 * 获取审计发现统计
 */
export function getAuditFindingsStats() {
  try {
    const data = localStorage.getItem('audit_findings');
    if (!data) return { total: 0, bySeverity: {}, byType: {}, byStatus: {} };
    
    const findings = JSON.parse(data);
    const bySeverity = {};
    const byType = {};
    const byStatus = {};
    
    findings.forEach(f => {
      bySeverity[f.severity] = (bySeverity[f.severity] || 0) + 1;
      byType[f.type] = (byType[f.type] || 0) + 1;
      byStatus[f.status] = (byStatus[f.status] || 0) + 1;
    });
    
    return { total: findings.length, bySeverity, byType, byStatus };
  } catch (e) {
    return { total: 0, bySeverity: {}, byType: {}, byStatus: {} };
  }
}

/**
 * 获取任务的完整执行数据
 */
export function getTaskExecutionData(taskId) {
  const executionData = {
    task: null,
    findings: [],
    rectifications: [],
    workpapers: [],
    team: []
  };
  
  try {
    // 获取任务信息
    const projectData = localStorage.getItem('audit_kanban_data');
    if (projectData) {
      const parsed = JSON.parse(projectData);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      
      for (const project of projects) {
        const task = project.tasks?.find(t => t.id === taskId);
        if (task) {
          executionData.task = { ...task, projectId: project.id, projectName: project.name };
          executionData.team = project.team || [];
          break;
        }
      }
    }
    
    // 获取审计发现
    const findingsData = localStorage.getItem('audit_findings');
    if (findingsData) {
      const findings = JSON.parse(findingsData);
      executionData.findings = findings.filter(f => f.taskId === taskId);
    }
    
    // 获取关联的整改事项
    if (projectData && executionData.task) {
      const parsed = JSON.parse(projectData);
      const project = parsed.projects?.find(p => p.id === executionData.task.projectId);
      if (project?.rectifications) {
        executionData.rectifications = project.rectifications.filter(r => 
          executionData.findings.some(f => f.id === r.findingId)
        );
      }
    }
  } catch (e) {
    console.error('获取任务执行数据失败:', e);
  }
  
  return executionData;
}

/**
 * 获取风险关联的所有数据
 */
export function getRiskRelatedData(riskId) {
  const related = {
    risk: null,
    projects: [],
    tasks: [],
    rectifications: [],
    controls: []
  };
  
  try {
    // 获取风险本身
    const riskData = localStorage.getItem('riskMatrixData');
    if (riskData) {
      const risks = JSON.parse(riskData);
      related.risk = risks.find(r => r.id === riskId || r.thirdLevelRiskId === riskId);
    }
    
    // 获取关联的项目和任务
    const projectData = localStorage.getItem('audit_kanban_data');
    if (projectData) {
      const parsed = JSON.parse(projectData);
      const projects = Array.isArray(parsed) ? parsed : (parsed.projects || []);
      
      projects.forEach(p => {
        // 检查项目是否关联了该风险
        if (p.linkedRisks?.includes(riskId)) {
          related.projects.push({
            id: p.id,
            name: p.name,
            status: p.status
          });
        }
        
        // 检查任务
        (p.tasks || []).forEach(t => {
          if (t.sourceType === 'risk' && t.sourceId === riskId) {
            related.tasks.push({
              ...t,
              projectId: p.id,
              projectName: p.name
            });
          }
        });
        
        // 检查整改事项
        (p.rectifications || []).forEach(r => {
          if (r.sourceType === 'risk' && r.sourceId === riskId) {
            related.rectifications.push({
              ...r,
              projectId: p.id,
              projectName: p.name
            });
          }
        });
      });
    }
    
    // 获取内控信息
    if (related.risk?.internalControl) {
      related.controls.push({
        name: related.risk.internalControl,
        effectiveness: related.risk.isControlEffective,
        department: related.risk.responsibleDept
      });
    }
  } catch (e) {
    console.error('获取风险关联数据失败:', e);
  }
  
  return related;
}

/**
 * 获取项目的完整数据视图
 */
export function getProjectFullView(projectId) {
  try {
    const data = localStorage.getItem('audit_kanban_data');
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    const project = parsed.projects?.find(p => p.id === projectId);
    if (!project) return null;
    
    // 获取关联的风险详情
    const riskData = localStorage.getItem('riskMatrixData');
    let linkedRisksDetails = [];
    if (riskData && project.linkedRisks) {
      const risks = JSON.parse(riskData);
      linkedRisksDetails = risks.filter(r => 
        project.linkedRisks.includes(r.id || r.thirdLevelRiskId)
      );
    }
    
    // 获取团队成员详情
    const auditorData = localStorage.getItem('ippf_auditors_data');
    let teamDetails = [];
    if (auditorData && project.team) {
      const auditors = JSON.parse(auditorData);
      teamDetails = project.team.map(m => {
        const auditor = auditors.find(a => a.id === m.auditorId);
        return {
          ...m,
          auditorInfo: auditor ? {
            name: auditor.name,
            level: auditor.level,
            department: auditor.department,
            skills: auditor.skills
          } : null
        };
      });
    }
    
    return {
      ...project,
      linkedRisksDetails,
      teamDetails,
      stats: {
        totalTasks: project.tasks?.length || 0,
        completedTasks: project.tasks?.filter(t => t.stageId === 'completed').length || 0,
        totalRectifications: project.rectifications?.length || 0,
        completedRectifications: project.rectifications?.filter(r => r.status === 'completed').length || 0,
        totalWorkpapers: project.workpapers?.items?.length || 0,
        approvedWorkpapers: project.workpapers?.items?.filter(w => w.status === 'approved').length || 0
      }
    };
  } catch (e) {
    console.error('获取项目完整视图失败:', e);
    return null;
  }
}

/**
 * 导出审计数据包（用于跨模块数据交换）
 */
export function exportAuditDataPackage() {
  const dataPackage = {
    version: '1.0',
    exportedAt: new Date().toISOString(),
    projects: null,
    risks: null,
    auditors: null,
    assessments: null
  };
  
  try {
    const projectData = localStorage.getItem('audit_kanban_data');
    if (projectData) dataPackage.projects = JSON.parse(projectData);
    
    const riskData = localStorage.getItem('riskMatrixData');
    if (riskData) dataPackage.risks = JSON.parse(riskData);
    
    const auditorData = localStorage.getItem('ippf_auditors_data');
    if (auditorData) dataPackage.auditors = JSON.parse(auditorData);
    
    const assessmentData = localStorage.getItem('ippf_assessments');
    if (assessmentData) dataPackage.assessments = JSON.parse(assessmentData);
  } catch (e) {
    console.error('导出数据包失败:', e);
  }
  
  auditEventBus.emit(AUDIT_EVENTS.DATA_EXPORT, dataPackage);
  return dataPackage;
}

/**
 * 数据一致性检查
 */
export function checkDataConsistency() {
  const issues = [];
  
  try {
    const projectData = localStorage.getItem('audit_kanban_data');
    const riskData = localStorage.getItem('riskMatrixData');
    const auditorData = localStorage.getItem('ippf_auditors_data');
    
    if (projectData && riskData) {
      const parsed = JSON.parse(projectData);
      const risks = JSON.parse(riskData);
      const riskIds = new Set(risks.map(r => r.id || r.thirdLevelRiskId));
      
      (parsed.projects || []).forEach(p => {
        // 检查关联的风险是否存在
        (p.linkedRisks || []).forEach(riskId => {
          if (!riskIds.has(riskId)) {
            issues.push({
              type: 'missing_risk',
              projectId: p.id,
              projectName: p.name,
              riskId,
              message: `项目关联的风险 ${riskId} 不存在`
            });
          }
        });
        
        // 检查任务来源
        (p.tasks || []).forEach(t => {
          if (t.sourceType === 'risk' && !riskIds.has(t.sourceId)) {
            issues.push({
              type: 'orphan_task',
              projectId: p.id,
              taskId: t.id,
              taskTitle: t.title,
              message: `任务的源风险 ${t.sourceId} 不存在`
            });
          }
        });
      });
    }
    
    if (projectData && auditorData) {
      const parsed = JSON.parse(projectData);
      const auditors = JSON.parse(auditorData);
      const auditorIds = new Set(auditors.map(a => a.id));
      
      (parsed.projects || []).forEach(p => {
        // 检查团队成员是否存在
        (p.team || []).forEach(m => {
          if (!auditorIds.has(m.auditorId)) {
            issues.push({
              type: 'missing_auditor',
              projectId: p.id,
              projectName: p.name,
              auditorId: m.auditorId,
              message: `项目团队成员 ${m.auditorId} 不存在`
            });
          }
        });
      });
    }
  } catch (e) {
    console.error('数据一致性检查失败:', e);
  }
  
  return issues;
}

export default {
  auditEventBus,
  AUDIT_EVENTS,
  getProjectStats,
  getRiskStats,
  getWorkpaperStats,
  getRectificationStats,
  getIPPFStats,
  getAuditFindingsStats,
  createTaskFromRisk,
  createTaskFromRectification,
  createControlTestFromRisk,
  createRectificationFromRisk,
  createRectificationFromFinding,
  linkRiskToProject,
  getProjectLinkedRisks,
  assignAuditorToProject,
  getAuditorProjects,
  getRiskRelatedData,
  getProjectFullView,
  getTaskExecutionData,
  getDashboardData,
  navigateToModule,
  runWorkflowAction,
  AUDIT_PORTAL_WORKFLOW_ACTION_EVENT,
  globalSearch,
  exportAuditDataPackage,
  checkDataConsistency
};
